import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "ui/label";
import { Badge } from "ui/badge";
import {
  ArrowLeft,
  Plane,
  FileText,
  MapPin,
  ArrowRight,
  Plus,
  Trash2,
  PencilLine,
  BookOpenCheck,
  ShieldCheck,
  Calendar,
  Check,
  MessageSquareText,
  Send,
  LockKeyhole,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { toast } from "sonner";
import { FullScreenLoading } from "components/ui/loader";
import {
  getTourByPublicId,
  getVenues,
  TourDetailApi,
  updateTour,
  updateTourStatus,
  requestVenueAvailability,
  UpdateTourPayload,
  TourAvailabilityRecommendationApi,
  HotelPropertyApi,
  getHotelsByVenue,
} from "services/tourService";
import { CustomDayPicker } from "ui/CustomDayPicker";
import {
  calculateVenueSchedule,
  getIntercityCheckout,
  getTotalVenueBuffer,
  getTravelTimeMinutes,
  normalizeTime,
  parseTimeToMinutes,
  canAccommodateAnotherVenue,
} from "utils/venueScheduleCalculator";
import {
  BufferConfig,
  getBufferConfigurations,
  VenueApi,
  PortApi,
  getPorts,
  getCutoffConfigurations,
  CutoffConfig,
  getPortByPublicId,
  getTourOperatorWithDocuments,
  IntercityCheckoutApi,
  getIntercityCheckouts,
  getTourChats,
  tourChatsubmittion,
  TourChat,
  SendTourConversationPayload,
} from "services/tourService";
import { getGeneralMasterByCode } from "services/tourService";
import { useAuth } from "contexts/AuthContext";
import { usePageHeader } from "contexts/PageHeaderContext";
import { Switch } from "ui/switch";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "ui/accordion";

interface TourViewEditPageProps {
  onNavigate?: (page: string) => void;
  tourId?: string;
}

interface FormData {
  tourType: string;
  packageId?: string;
  packageName?: string;
  arrivalTransportMode: "flight" | "road" | "sea";
  arrivalAirport: number;
  arrivalDate: string;
  arrivalTime: string;
  arrivalFlightNumber: string;
  arrivalBookingRef: string;
  arrivalAirline: string;
  arrivalConfirmed: boolean;
  departureTransportMode: "flight" | "road" | "sea";
  departureAirport: number;
  departureDate: string;
  departureTime: string;
  departureFlightNumber: string;
  departureBookingRef: string;
  departureAirline: string;
  departureConfirmed: boolean;
  tourName: string;
  parentTourId: string;
  noOfApplicants: string;
  faizAccommodation: "yes" | "no";
  accommodationPreference: "minimum" | "maximum";
  specialAccommodationRequest: string;
  termsAndConditions: string;
  notes: string;
  venues: Array<{
    id: string;
    venueId: number | null;
    hotelPropertyId: number | null;
    hotelName: string;
    fromDate: string;
    fromTime: string;
    toDate: string;
    toTime: string;
  }>;
}

interface ReviewMessage {
  id: string;
  sender: "backoffice" | "tour_operator";
  senderName: string;
  message: string;
  timestamp: string;
  dateTime: string;
}

enum TourType {
  GENERAL = 1,
  MIQAAT = 2,
}

export default function TourViewEditPage({
  onNavigate,
  tourId,
}: TourViewEditPageProps) {
  const [activeTab, setActiveTab] = useState<
    "arrival-departure" | "venue-summary" | "confirmation"
  >("arrival-departure");

  // Arrival state
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { user } = useAuth();
  const { id } = useParams();
  const [, setTourData] = useState<TourDetailApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [, setVenueOptions] = useState<{ id: string; name: string }[]>([]);
  const enabledTabs = ["arrival-departure", "venue-summary", "confirmation"];
  const currentIndex = enabledTabs.indexOf(activeTab);
  const [tourStatus, setTourStatus] = useState<number | null>(null);
  const [tourCategory, setTourCategory] = useState<number | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [venuesFromApi, setVenuesFromApi] = useState<VenueApi[]>([]);
  const [buffers, setBuffers] = useState<BufferConfig[]>([]);
  const [ports, setPorts] = useState<PortApi[]>([]);
  const [cutoffs, setCutoffs] = useState<CutoffConfig[]>([]);
  const [cutoffDates, setCutoffDates] = useState<string[]>([]);
  const [isVenueManuallyChanged, setIsVenueManuallyChanged] = useState(false);
  const [portBlocks, setPortBlocks] = useState<Record<number, Date[]>>({});
  const [operatorExpiryDate, setOperatorExpiryDate] = useState<Date | null>(
    null,
  );

  const [arrivalCalendarMonth, setArrivalCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [departureCalendarMonth, setDepartureCalendarMonth] = useState<
    Date | undefined
  >(undefined);
  const [venueCalendarMonths, setVenueCalendarMonths] = useState<
    Record<string, Date | undefined>
  >({});

  const [arrivalConfirmedFromApi, setArrivalConfirmedFromApi] = useState(false);
  const [departureConfirmedFromApi, setDepartureConfirmedFromApi] =
    useState(false);
  const [manualDateEdit, setManualDateEdit] = useState(false);

  const [accommodateBy, setAccommodateBy] = useState<"minimum" | "maximum">(
    "maximum",
  );
  const [specialRoomRequestDetails, setSpecialRoomRequestDetails] =
    useState<string>("");

  const [termsAndConditions, setTermsAndConditions] = useState("");

  const portBlockedDates = Object.values(portBlocks).flat();
  const cutoffBlockedDates = cutoffDates.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  });

  const allBlockedDates = [...portBlockedDates, ...cutoffBlockedDates];
  const tourOperatorId = user?.tourOperatorId ?? null;
  const [isInitialized, setIsInitialized] = useState(false);
  const [rotationId, setRotationId] = useState<number | null>(null);

  const [termsAccepted, setTermsAccepted] = useState(false);

  const [reviewConversation, setReviewConversation] = useState<ReviewMessage[]>(
    [],
  );
  const [responseToAdmin, setResponseToAdmin] = useState("");

  const [hotelsByVenue, setHotelsByVenue] = useState<
    Record<number, HotelPropertyApi[]>
  >({});

  useEffect(() => {
    if (tourStatus === 2 || tourStatus === 4) {
      setTermsAccepted(true);
    }
  }, [tourStatus]);

  const loadTourChats = useCallback(
    async (showError = false) => {
      try {
        if (!id) return;

        const response = await getTourChats(id);
        console.log("Messages", response.data);

        const chats: ReviewMessage[] =
          response?.data?.map(
            (item: TourChat): ReviewMessage => ({
              id: item.publicId,
              sender: item.isAdmin ? "backoffice" : "tour_operator",
              senderName: item.senderName,
              message: item.message,
              timestamp: new Date(item.createdOn).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
              dateTime: new Date(item.createdOn).toLocaleDateString(),
            }),
          ) ?? [];

        setReviewConversation(chats);
      } catch (error) {
        console.error(error);
        if (showError) {
          toast.error("Failed to load chat.");
        }
      }
    },
    [id],
  );

  useEffect(() => {
    if (id) {
      loadTourChats(false);
    }
  }, [id, loadTourChats]);

  const handleSendReviewResponse = async () => {
    const message = responseToAdmin.trim();

    if (!message) {
      toast.error("Empty message is not allowed.");
      return;
    }

    try {
      setLoading(true);

      const payload: SendTourConversationPayload = {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tourPublicId: id!,
        message: message,
        isInternal: false,
      };

      const response = await tourChatsubmittion(payload);

      if (!response.success) {
        toast.error(response.message);
        return;
      }

      toast.success("Message sent successfully.");

      setResponseToAdmin("");

      await loadTourChats();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  const [recommendedDates, setRecommendedDates] = useState<
    TourAvailabilityRecommendationApi[]
  >([]);
  const [selectedRecommendedDate, setSelectedRecommendedDate] = useState<
    string | null
  >(null);

  const getTourType = () => {
    return tourCategory === 2 ? TourType.MIQAAT : TourType.GENERAL;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getTourCategory = () => {
    const tourType = getTourType();

    switch (tourType) {
      case TourType.GENERAL:
        return formData.faizAccommodation === "yes" ? 3 : 4;

      case TourType.MIQAAT:
        return 2;

      default:
        return null;
    }
  };

  const location = useLocation();
  const tourCode = location.state?.tourCode;
  const searchParams = new URLSearchParams(location.search);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const flowType = location.state?.flowType;

  const previousBookingFilter = searchParams.get("bookingFilter") || "current";
  const selectedMiqaat = location.state?.selectedMiqaat;
  const [intercityCheckouts, setIntercityCheckouts] = useState<
    IntercityCheckoutApi[]
  >([]);

  const parseLocalDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(y, m - 1, d);
  };

  const mapTransportMode = (mode: "flight" | "road" | "sea") => {
    if (mode === "flight") return 1;
    if (mode === "road") return 2;
    if (mode === "sea") return 3;
    return 1;
  };
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isMiqaat = getTourType() === TourType.MIQAAT;

  const [formData, setFormData] = useState<FormData>({
    tourType: "",
    arrivalTransportMode: "flight",
    arrivalAirport: 0,
    arrivalDate: "",
    arrivalTime: "",
    arrivalFlightNumber: "",
    arrivalBookingRef: "",
    arrivalAirline: "",
    arrivalConfirmed: false,
    departureTransportMode: "flight",
    departureAirport: 0,
    departureDate: "",
    departureTime: "",
    departureFlightNumber: "",
    departureBookingRef: "",
    departureAirline: "",
    departureConfirmed: false,
    tourName: "",
    parentTourId: "",
    noOfApplicants: "",
    faizAccommodation: "no",
    accommodationPreference: "maximum",
    specialAccommodationRequest: "",
    termsAndConditions: "",
    notes: "",
    venues: [
      {
        id: "venue-1",
        venueId: null,
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
      {
        id: "venue-2",
        venueId: null,
        hotelPropertyId: null,
        hotelName: "",
        fromDate: "",
        fromTime: "",
        toDate: "",
        toTime: "",
      },
    ],
  });

  const formatDateSafe = (dateStr?: string) => {
    if (!dateStr) return "";

    const [datePart] = dateStr.split("T");
    return datePart;
  };

  const buildUpdatePayload = (): UpdateTourPayload => {
    return {
      parent_id: formData.parentTourId.trim() || null,
      tourName: formData.tourName,
      tourType: getTourType(),
      tourCategory: tourCategory,
      rotationId: tourCategory === 2 ? rotationId : null,
      tourOperatorId: user?.tourOperatorId || 0,
      noOfApplicants: Number(formData.noOfApplicants),
      faizAccommodation: formData.faizAccommodation === "yes",
      ...(formData.faizAccommodation === "yes" && {
        accommodationPreference: accommodateBy === "minimum" ? 1 : 2,
        specialAccommodationRequest: specialRoomRequestDetails.trim(),
      }),
      termsAndConditions: formData.termsAndConditions,
      notes: formData.notes,
      arrivalTransportMode: mapTransportMode(formData.arrivalTransportMode),
      arrivalFlightNo: formData.arrivalFlightNumber,
      arrivalTime: normalizeTime(formData.arrivalTime),
      arrivalPortId: formData.arrivalAirport,
      arrivalDate: `${formData.arrivalDate}T00:00:00`,
      arrivalBookingRef: formData.arrivalBookingRef,
      arrivalAirline: formData.arrivalAirline,

      departureTransportMode: mapTransportMode(formData.departureTransportMode),
      departureFlightNo: formData.departureFlightNumber,
      departureTime: normalizeTime(formData.departureTime),
      departurePortId: formData.departureAirport,
      departureDate: `${formData.departureDate}T00:00:00`,
      departureBookingRef: formData.departureBookingRef,
      departureAirline: formData.departureAirline,

      isArrivalConfirmed: formData.arrivalConfirmed,
      isDepartureConfirmed: formData.departureConfirmed,

      venueSchedules: formData.venues.map((v) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        venueId: v.venueId!,
        ...(formData.faizAccommodation === "no" && {
          ...(v.hotelPropertyId
            ? {
                hotelPropertyId: v.hotelPropertyId,
                hotelName: v.hotelName,
              }
            : {}),
        }),
        checkInDate: `${v.fromDate}T00:00:00`,
        checkInTime: v.fromTime,
        checkOutDate: `${v.toDate}T00:00:00`,
        checkOutTime: v.toTime,
      })),

      updatedBy: 1,
    };
  };

  const handleAcceptRecommendedDate = (
    dateOption: (typeof recommendedDates)[0],
  ) => {
    setSelectedRecommendedDate(dateOption.recommendPublicId);

    setFormData((prev) => ({
      ...prev,
      arrivalDate: dateOption.requestedStartDate,
      departureDate: dateOption.requestedEndDate,
    }));

    setManualDateEdit(false);

    setIsVenueManuallyChanged(false);

    toast.success("Recommended dates applied to arrival and departure.");
  };

  const loadTermsAndConditions = async () => {
    try {
      if (!tourCategory) return;

      let code = "";

      switch (tourCategory) {
        case 2:
          code = "TOUR_TNC_Miqaat";
          break;

        case 3:
          code = "TOUR_TNC_Faiz";
          break;

        case 4:
          code = "TOUR_TNC_Hotel";
          break;

        default:
          code = "TOUR_TNC_Instant";
          break;
      }

      const res = await getGeneralMasterByCode(code);

      setTermsAndConditions(res.value);

      setFormData((prev) => ({
        ...prev,
        termsAndConditions: res.value,
      }));
    } catch (error) {
      console.error("Failed to load T&C", error);
    }
  };

  useEffect(() => {
    const fetchTour = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const data = await getTourByPublicId(id!);
        console.log("FETCHED:", data);
        setTourData(data);
        mapApiToState(data);
      } catch (err) {
        toast.error("Failed to load tour");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchTour();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!venuesFromApi.length) return;
    const loadHotels = async () => {
      const result: Record<number, HotelPropertyApi[]> = {};
      for (const venue of formData.venues) {
        if (!venue.venueId) continue;
        const selectedVenue = venuesFromApi.find(
          (v) => v.venueId === venue.venueId,
        );
        if (!selectedVenue) continue;
        result[venue.venueId] = await getHotelsByVenue(selectedVenue.publicId);
      }
      setHotelsByVenue(result);
    };
    loadHotels();
  }, [venuesFromApi, formData.venues]);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const res = await getVenues();

        setVenuesFromApi(res);

        const mapped = res.map((v) => ({
          id: String(v.venueId),
          name: v.venueName,
        }));

        setVenueOptions(mapped);
      } catch {
        toast.error("Failed to load venues");
      }
    };

    fetchVenues();
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isConfirmed = formData.arrivalConfirmed || formData.departureConfirmed;

  useEffect(() => {
    if (!isInitialized) return;
    if (getTourType() === TourType.MIQAAT) return;
    if (formData.venues.some((v) => !v.venueId)) return;
    if (manualDateEdit) return;

    if (
      !isValidTime(formData.arrivalTime) ||
      !isValidTime(formData.departureTime)
    ) {
      return;
    }

    const updatedVenues = calculateVenueSchedule({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });

    if (!updatedVenues) return;

    const isSame =
      JSON.stringify(formData.venues) === JSON.stringify(updatedVenues);

    if (isSame) return;

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        ...updatedVenues[i],
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalDate,
    formData.arrivalTime,
    formData.departureDate,
    formData.departureTime,
    buffers,
    intercityCheckouts,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formData.venues),
  ]);

  useEffect(() => {
    const fetchBuffers = async () => {
      try {
        const data = await getBufferConfigurations();
        setBuffers(data.filter((b) => b.isActive));
      } catch (e) {
        console.error("Buffer fetch failed", e);
      }
    };

    fetchBuffers();
  }, []);

  useEffect(() => {
    const fetchAllPortBlocks = async () => {
      try {
        if (!ports.length) return;

        const results = await Promise.all(
          ports.map((port) => getPortByPublicId(port.publicId)),
        );

        const allBlocks: Record<number, Date[]> = {};

        results.forEach((result, index) => {
          const port = ports[index];

          const dates: Date[] = [];

          result.blocks.forEach((block) => {
            const start = new Date(block.fromDate.split("T")[0]);
            const end = new Date(block.toDate.split("T")[0]);

            // eslint-disable-next-line prefer-const
            let current = new Date(start);

            while (current <= end) {
              dates.push(
                new Date(
                  current.getFullYear(),
                  current.getMonth(),
                  current.getDate(),
                ),
              );
              current.setDate(current.getDate() + 1);
            }
          });

          allBlocks[port.portId] = dates;
        });

        setPortBlocks(allBlocks);
      } catch (e) {
        console.error(e);
      }
    };

    fetchAllPortBlocks();
  }, [ports]);

  useEffect(() => {
    const fetchOperator = async () => {
      try {
        if (!tourOperatorId) return;

        const res = await getTourOperatorWithDocuments(tourOperatorId);

        const expiry = res.partners?.find(
          (p) => p.isMainOperator,
        )?.permissionExpireDate;

        if (expiry) {
          const [datePart] = expiry.split(" ");

          const [mm, dd, yyyy] = datePart.split("/").map(Number);

          const parsed = new Date(yyyy, mm - 1, dd);

          setOperatorExpiryDate(parsed);
        }
      } catch (e) {
        console.error("Failed to fetch operator", e);
      }
    };

    fetchOperator();
  }, [tourOperatorId]);

  const getBaseRoute = (arrivalPortId: number) => {
    const najafVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("najaf"),
    )?.venueId;

    const karbalaVenue = venuesFromApi.find((v) =>
      v.venueName.toLowerCase().includes("karbala"),
    )?.venueId;

    if (!najafVenue || !karbalaVenue) return [];

    const arrivalPort = ports.find((p) => p.portId === arrivalPortId);

    if (!arrivalPort) return [];

    const isNajafArrival = arrivalPort.portName.toLowerCase().includes("najaf");

    return isNajafArrival
      ? [najafVenue, karbalaVenue]
      : [karbalaVenue, najafVenue];
  };

  const [, setRefreshKey] = useState(0);

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalAirport]);

  useEffect(() => {
    if (!isInitialized) return;
    if (!venuesFromApi.length || !formData.arrivalAirport) return;
    if (formData.arrivalConfirmed) return;

    if (isVenueManuallyChanged) return;

    const route = getBaseRoute(formData.arrivalAirport);

    setFormData((prev) => ({
      ...prev,
      venues: prev.venues.map((v, i) => ({
        ...v,
        venueId: route[i] || v.venueId,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialized,
    formData.arrivalAirport,
    venuesFromApi,
    isVenueManuallyChanged,
  ]);

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const data = await getPorts();
        setPorts(data.filter((p) => p.isActive));
      } catch (e) {
        console.error("Port fetch failed", e);
      }
    };

    fetchPorts();
  }, []);

  useEffect(() => {
    const fetchCutoffs = async () => {
      try {
        const data = await getCutoffConfigurations();
        setCutoffs(data);
      } catch (e) {
        console.error("Cutoff fetch failed", e);
      }
    };

    fetchCutoffs();
  }, []);

  useEffect(() => {
    if (!cutoffs.length) {
      setCutoffDates([]);
      return;
    }
    const dates: string[] = [];
    const formatDate = (date: Date) => {
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const dd = String(date.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    };
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    cutoffs.forEach((c) => {
      const effectiveFrom = new Date(c.effectiveFrom.split("T")[0]);
      effectiveFrom.setHours(0, 0, 0, 0);

      const effectiveTo = new Date(c.effectiveTo.split("T")[0]);
      effectiveTo.setHours(0, 0, 0, 0);
      if (today < effectiveFrom || today > effectiveTo) {
        return;
      }
      const cutoffEndDate = new Date(today);
      cutoffEndDate.setDate(today.getDate() + c.daysBeforeCutoff - 1);
      if (cutoffEndDate > effectiveTo) {
        cutoffEndDate.setTime(effectiveTo.getTime());
      }
      const current = new Date(today);
      while (current <= cutoffEndDate) {
        dates.push(formatDate(current));
        current.setDate(current.getDate() + 1);
      }
    });
    const uniqueDates = [...new Set(dates)];
    setCutoffDates(uniqueDates);
  }, [cutoffs]);

  useEffect(() => {
    const fetchIntercity = async () => {
      try {
        const data = await getIntercityCheckouts();
        setIntercityCheckouts(data.filter((i) => i.isActive));
      } catch (e) {
        console.error("Intercity fetch failed", e);
      }
    };

    fetchIntercity();
  }, []);

  useEffect(() => {
    if (!selectedMiqaat) return;

    setFormData((prev) => {
      if (prev.arrivalDate || prev.departureDate) {
        return prev;
      }

      return {
        ...prev,
        arrivalDate: selectedMiqaat.arrivalDate?.split("T")[0] || "",
        departureDate: selectedMiqaat.departureDate?.split("T")[0] || "",
      };
    });
  }, [selectedMiqaat]);

  useEffect(() => {
    setIsVenueManuallyChanged(false);
  }, [formData.arrivalDate, formData.departureDate]);

  useEffect(() => {
    loadTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourCategory]);

  useEffect(() => {
    if (activeTab !== "confirmation") return;

    loadTermsAndConditions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const canAddVenue = () => {
    if (
      !formData.arrivalDate ||
      !formData.arrivalTime ||
      !formData.departureDate ||
      !formData.departureTime
    ) {
      return false;
    }

    if (formData.venues.some((v) => !v.venueId)) {
      return false;
    }

    return canAccommodateAnotherVenue({
      venues: formData.venues,
      arrivalDate: formData.arrivalDate,
      arrivalTime: formData.arrivalTime,
      departureDate: formData.departureDate,
      departureTime: formData.departureTime,
      buffers,
      intercityCheckouts,
      tourType: getTourType(),
    });
  };

  const getCalculationBreakdown = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    venue: any,
    index: number,
    type: "checkin" | "checkout",
  ) => {
    if (!venue.venueId) return null;

    const venueId = venue.venueId;

    const venueName =
      venuesFromApi.find((v) => v.venueId === venueId)?.venueName || "";

    const isLast = index === formData.venues.length - 1;

    let portId: number | undefined;

    if (index === 0) {
      portId = formData.arrivalAirport;
    } else if (isLast) {
      portId = formData.departureAirport;
    } else {
      portId = undefined;
    }

    let intercityTime: string | null = null;
    let intercityUsed = false;

    if (type === "checkout" && !isLast) {
      const nextVenue = formData.venues[index + 1];

      const intercity = nextVenue?.venueId
        ? getIntercityCheckout(
            intercityCheckouts,
            venueId,
            nextVenue.venueId,
            parseLocalDate(venue.toDate),
            getTourType(),
          )
        : null;

      if (intercity?.checkoutTime) {
        intercityTime = intercity.checkoutTime;
        intercityUsed = true;
      }

      // MIQAAT fallback (ONLY if no intercity from API)
      if (getTourType() === TourType.MIQAAT && !intercityUsed) {
        intercityTime = venue.toTime;
        intercityUsed = true;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiqaat = getTourType() === TourType.MIQAAT;

    const isIntercityCase = type === "checkout" && !isLast && intercityUsed;

    let hasTravel = false;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity = getIntercityCheckout(
          intercityCheckouts,
          prevVenue.venueId,
          venue.venueId,
          parseLocalDate(prevVenue.toDate),
          getTourType(),
        );

        if (intercity?.travelTime) {
          hasTravel = true;
        }
      }
    }

    const buffersData = getTotalVenueBuffer(
      buffers,
      venueId,
      portId,
      !(isIntercityCase || hasTravel),
    );

    const accommodationBuffers = buffers.filter(
      (b) => b.venueId === venueId && b.type === "1",
    );

    const transportBuffers = !(isIntercityCase || hasTravel)
      ? buffers.filter((b) => b.venueId === venueId && b.type === "2")
      : [];

    const sumBuffers = (
      arr: BufferConfig[],
      key: "checkInBuffer" | "checkOutBuffer",
    ) =>
      arr.reduce(
        (total, b) => total + parseTimeToMinutes(b[key] || "00:00"),
        0,
      );

    const formatHours = (minutes: number) => `${Math.floor(minutes / 60)} hrs`;

    const accommodationMinutes = sumBuffers(
      accommodationBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    const transportMinutes = sumBuffers(
      transportBuffers,
      type === "checkin" ? "checkInBuffer" : "checkOutBuffer",
    );

    let travelMinutes = 0;

    if (type === "checkin" && index > 0) {
      const prevVenue = formData.venues[index - 1];

      if (prevVenue?.venueId) {
        const intercity =
          prevVenue?.venueId && venue.venueId
            ? getIntercityCheckout(
                intercityCheckouts,
                prevVenue.venueId,
                venue.venueId,
                new Date(prevVenue.toDate),
                getTourType(),
              )
            : null;

        if (intercity?.travelTime) {
          travelMinutes = getTravelTimeMinutes(intercity.travelTime);
        }
      }
    }

    const totalMinutes =
      (isIntercityCase
        ? accommodationMinutes
        : type === "checkin"
          ? buffersData.checkIn
          : buffersData.checkOut) + travelMinutes;

    const rawBaseTime =
      type === "checkin"
        ? index === 0
          ? formData.arrivalTime
          : venue.fromTime
        : isLast
          ? formData.departureTime
          : intercityTime || venue.toTime;

    const baseTime = normalizeTime(rawBaseTime || "");

    return {
      venueName,
      baseTime,
      accommodation: formatHours(accommodationMinutes),
      transport: formatHours(transportMinutes),
      totalBuffer: formatHours(totalMinutes),
      hasTransport: !isIntercityCase && transportMinutes > 0,
      usedIntercity: intercityUsed,
      travel:
        travelMinutes > 0
          ? formatHours(travelMinutes)
          : getTourType() === TourType.MIQAAT
            ? "1 hrs"
            : null,
    };
  };

  const CalculationBox = ({
    data,
    type,
    index,
    isLast,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    type: "checkin" | "checkout";
    index: number;
    isLast: boolean;
  }) => {
    if (!data) return null;

    const isFirst = index === 0;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const isMiddle = !isFirst && !isLast;

    let label: string | null = null;

    if (type === "checkin") {
      if (isFirst) {
        label = "Arrival Time";
      } else {
        label = null;
      }
    } else {
      // checkout
      if (data.usedIntercity) {
        label = "Intercity Checkout";
      } else if (isLast) {
        label = "Departure Time";
      } else {
        label = null;
      }
    }

    return (
      <div
        className="absolute z-10 left-0 mt-2 w-64 p-3 rounded-md bg-blue-50 border border-blue-200 text-xs text-gray-700 space-y-1 
          opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 shadow-lg pointer-events-none"
      >
        <div className="font-medium text-blue-700">Calculation Breakdown</div>
        {label && (
          <div>
            {label}: <span className="font-semibold">{data.baseTime}</span>
          </div>
        )}

        <div>
          Accommodation Buffer:{" "}
          <span className="font-semibold">{data.accommodation}</span>
        </div>

        {data.hasTransport && (
          <div>
            Transport Buffer:{" "}
            <span className="font-semibold">{data.transport}</span>
          </div>
        )}

        {data.travel && (
          <div>
            Travel Time: <span className="font-semibold">{data.travel}</span>
          </div>
        )}

        <div className="pt-1 border-t">
          Total Buffer:{" "}
          <span className="font-semibold text-blue-700">
            {data.totalBuffer}
          </span>
        </div>
      </div>
    );
  };

  const mapTransportModeFromApi = (
    mode?: number,
  ): "flight" | "road" | "sea" => {
    switch (mode) {
      case 1:
        return "flight";
      case 2:
        return "road";
      case 3:
        return "sea";
      default:
        return "flight";
    }
  };

  const mapApiToState = (data: TourDetailApi) => {
    setTourCategory(data.tourCategory);
    setRotationId(data.rotationId);

    setIsLocked(data.isLocked ?? false);

    setArrivalConfirmedFromApi(data.isArrivalConfirmed ?? false);
    setDepartureConfirmedFromApi(data.isDepartureConfirmed ?? false);
    const mappedVenues = (data.venueSchedules || []).map((v, i) => ({
      id: v.publicId || `venue-${i}`,
      venueId: v.venueId,
      hotelPropertyId: v.hotelPropertyId ?? null,
      hotelName: v.hotelName ?? "",
      fromDate: formatDateSafe(v.checkInDate),
      fromTime: v.checkInTime || "",
      toDate: formatDateSafe(v.checkOutDate),
      toTime: v.checkOutTime || "",
    }));

    setFormData((prev) => ({
      ...prev,
      rotationId: data.rotationId,
      arrivalTransportMode: mapTransportModeFromApi(data.arrivalTransportMode),
      departureTransportMode: mapTransportModeFromApi(
        data.departureTransportMode,
      ),
      arrivalAirport: data.arrivalPortId || 0,
      arrivalDate: data.arrivalDate?.split("T")[0] || "",
      arrivalTime: data.arrivalTime || "",
      arrivalFlightNumber: data.arrivalFlightNo || "",
      arrivalBookingRef: data.arrivalBookingRef || "",
      arrivalAirline: data.arrivalAirline || "",
      arrivalConfirmed: data.isArrivalConfirmed ?? false,

      departureAirport: data.departurePortId || 0,
      departureDate: data.departureDate?.split("T")[0] || "",
      departureTime: data.departureTime || "",
      departureFlightNumber: data.departureFlightNo || "",
      departureBookingRef: data.departureBookingRef || "",
      departureAirline: data.departureAirline || "",
      departureConfirmed: data.isDepartureConfirmed ?? false,

      tourName: data.tourName || "",
      parentTourId: data.parent_id != null ? String(data.parent_id) : "",
      noOfApplicants: String(data.noOfApplicants || ""),
      faizAccommodation: data.faizAccommodation ? "yes" : "no",

      accommodationPreference:
        data.accommodationPreference === 1 ? "minimum" : "maximum",

      specialAccommodationRequest: data.specialAccommodationRequest || "",

      notes: data.notes || "",

      venues: mappedVenues,
      tourType: String(data.tourType || ""),
    }));

    if (data.faizAccommodation) {
      setAccommodateBy(
        data.accommodationPreference === 1 ? "minimum" : "maximum",
      );

      setSpecialRoomRequestDetails(data.specialAccommodationRequest || "");
    } else {
      setAccommodateBy("maximum");
      setSpecialRoomRequestDetails("");
    }

    setRecommendedDates(data.tourAvailabilityRecommendations ?? []);

    setTourStatus(data.status);
    if (data.status === 1) {
      setActiveTab("confirmation");
    } else {
      setActiveTab("arrival-departure");
    }
    setIsInitialized(true);
  };

  const getStatusName = (status: number | null) => {
    switch (status) {
      case 1:
        return "Draft";

      case 2:
        return "Submitted";

      case 3:
        return "Alternative Dates Proposed";

      case 4:
        return "Resubmitted";

      case 5:
        return "Confirmed";

      case 6:
        return "Cancelled";

      case 7:
        return "Checked In";

      case 8:
        return "Checked Out";

      case 9:
        return "Completed";

      default:
        return "-";
    }
  };

  const getStatusMeta = (status: number | null) => {
    switch (status) {
      case 1:
        return {
          className: "bg-gray-100 text-gray-700 border-gray-300",
        };

      case 2:
        return {
          className: "bg-blue-100 text-blue-700 border-blue-300",
        };

      case 3:
        return {
          className: "bg-orange-100 text-orange-700 border-orange-300",
        };

      case 4:
        return {
          className: "bg-indigo-100 text-indigo-700 border-indigo-300",
        };

      case 5:
        return {
          className: "bg-green-100 text-green-700 border-green-300",
        };

      case 6:
        return {
          className: "bg-red-100 text-red-700 border-red-300",
        };

      case 7:
        return {
          className: "bg-cyan-100 text-cyan-700 border-cyan-300",
        };

      case 8:
        return {
          className: "bg-purple-100 text-purple-700 border-purple-300",
        };

      case 9:
        return {
          className: "bg-emerald-100 text-emerald-700 border-emerald-300",
        };

      default:
        return {
          className: "bg-gray-100 text-gray-700 border-gray-300",
        };
    }
  };

  const statusMeta = getStatusMeta(tourStatus);
  const statusName = getStatusName(tourStatus);

  const isReadOnly = tourStatus === 2 || tourStatus === 4;

  const formatLocalDate = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const isDateDisabled = (date: Date, type: "arrival" | "departure") => {
    const current = new Date(date);
    current.setHours(0, 0, 0, 0);

    if (selectedMiqaat?.type === "package") {
      const rotationStart = new Date(selectedMiqaat.arrivalDate.split("T")[0]);

      const rotationEnd = new Date(selectedMiqaat.departureDate.split("T")[0]);

      rotationStart.setHours(0, 0, 0, 0);
      rotationEnd.setHours(0, 0, 0, 0);

      return current < rotationStart || current > rotationEnd;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 3);

    if (current < minBookingDate) {
      return true;
    }

    if (operatorExpiryDate) {
      const expiry = new Date(operatorExpiryDate);
      expiry.setHours(0, 0, 0, 0);

      if (current > expiry) {
        return true;
      }
    }

    return false;
  };

  const FLIGHT_NO_REGEX = /^(?:[A-Z]{2}|[A-Z][0-9])[0-9]{1,4}$/;
  const isValidFlightNumber = (value: string) =>
    FLIGHT_NO_REGEX.test(value.trim());

  const TIME_24H_REGEX = /^([01]\d|2[0-3]):([0-5]\d)$/;
  const isValidTime = (value: string) => TIME_24H_REGEX.test(value);

  const AIRLINE_REGEX = /^[\p{L}\p{M}\p{N}\s-]{1,60}$/u;
  const isValidAirline = (value: string) => AIRLINE_REGEX.test(value.trim());

  const BOOKING_REF_REGEX = /^[A-Z0-9]{1,16}$/;
  const isValidBookingRef = (value: string) => BOOKING_REF_REGEX.test(value);

  const TOUR_NAME_REGEX = /^[A-Za-z0-9 ]{1,50}$/;

  const validateArrivalDeparture = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.arrivalDate) {
      newErrors.arrivalDate = "Arrival date is required";
    }

    if (!formData.arrivalTime) {
      newErrors.arrivalTime = "Arrival time is required";
    }

    if (formData.arrivalTime && !isValidTime(formData.arrivalTime)) {
      newErrors.arrivalTime = "Time must be in 24-hour format (HH:MM)";
    }

    if (!formData.arrivalAirport) {
      newErrors.arrivalAirport = "Arrival airport is required";
    }

    if (formData.arrivalTransportMode === "flight") {
      if (!formData.arrivalFlightNumber) {
        newErrors.arrivalFlightNumber = "Flight No. is required";
      } else if (!isValidFlightNumber(formData.arrivalFlightNumber)) {
        newErrors.arrivalFlightNumber = "Format must be AB1234 or A11234";
      }
    }

    if (formData.arrivalAirline && !isValidAirline(formData.arrivalAirline)) {
      newErrors.arrivalAirline =
        "Only letters, numbers, spaces and hyphens are allowed (maximum 60 characters).";
    }

    if (
      formData.arrivalBookingRef &&
      !isValidBookingRef(formData.arrivalBookingRef)
    ) {
      newErrors.arrivalBookingRef =
        "Booking Ref must be alphanumeric and max 16 characters";
    }

    if (!formData.departureDate) {
      newErrors.departureDate = "Departure date is required";
    }

    if (!formData.departureTime) {
      newErrors.departureTime = "Departure time is required";
    }

    if (formData.departureTime && !isValidTime(formData.departureTime)) {
      newErrors.departureTime = "Time must be in 24-hour format (HH:MM)";
    }

    if (!formData.departureAirport) {
      newErrors.departureAirport = "Departure airport is required";
    }

    if (formData.departureTransportMode === "flight") {
      if (!formData.departureFlightNumber) {
        newErrors.departureFlightNumber = "Flight No. is required";
      } else if (!isValidFlightNumber(formData.departureFlightNumber)) {
        newErrors.departureFlightNumber = "Format must be AB1234 or A11234";
      }
    }

    if (
      formData.departureAirline &&
      !isValidAirline(formData.departureAirline)
    ) {
      newErrors.departureAirline =
        "Only letters, numbers, spaces and hyphens are allowed (maximum 60 characters).";
    }

    if (
      formData.departureBookingRef &&
      !isValidBookingRef(formData.departureBookingRef)
    ) {
      newErrors.departureBookingRef =
        "Booking Ref must be alphanumeric and max 16 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const validateVenueSummary = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.tourName.trim()) {
      newErrors.tourName = "Tour name is required";
    } else if (!TOUR_NAME_REGEX.test(formData.tourName.trim())) {
      newErrors.tourName =
        "Only letters and numbers allowed. Maximum 50 characters.";
    }

    if (
      formData.parentTourId.trim() &&
      !/^[A-Za-z0-9-]{1,50}$/.test(formData.parentTourId.trim())
    ) {
      newErrors.parentTourId =
        "Only letters, numbers and hyphens (-) are allowed. Maximum 50 characters.";
    }

    if (!formData.noOfApplicants) {
      newErrors.noOfApplicants = "Applicants required";
    } else {
      const applicants = Number(formData.noOfApplicants);

      if (applicants < 0) {
        newErrors.noOfApplicants = "Number of applicants cannot be negative";
      } else if (applicants > 200) {
        newErrors.noOfApplicants = "Number of applicants cannot exceed 200";
      }
    }

    formData.venues.forEach((venue, index) => {
      if (!venue.venueId) {
        newErrors[`venueName-${index}`] = "Venue required";
      }

      if (!venue.fromDate) {
        newErrors[`fromDate-${index}`] = "Check-in date required";
      }

      if (!venue.fromTime) {
        newErrors[`fromTime-${index}`] = "Check-in time required";
      }

      if (!venue.toDate) {
        newErrors[`toDate-${index}`] = "Check-out date required";
      }

      if (!venue.toTime) {
        newErrors[`toTime-${index}`] = "Check-out time required";
      }
    });

    for (let index = 1; index < formData.venues.length; index++) {
      const previousVenue = formData.venues[index - 1];
      const currentVenue = formData.venues[index];

      if (
        previousVenue.venueId &&
        currentVenue.venueId &&
        previousVenue.venueId === currentVenue.venueId
      ) {
        const venueName =
          venuesFromApi.find((v) => v.venueId === currentVenue.venueId)
            ?.venueName || "This venue";

        toast.error(
          `Venue ${index + 1} cannot be the same as Venue ${index}. Please select a different venue instead of ${venueName}.`,
        );

        return false;
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSave = async () => {
    try {
      if (!validateArrivalDeparture()) {
        setActiveTab("arrival-departure");
        return;
      }

      if (!validateVenueSummary()) {
        setActiveTab("venue-summary");
        return;
      }
      setLoading(true);
      if (!id) {
        toast.error("Missing tour ID");
        return;
      }

      await loadTermsAndConditions();

      const payload = buildUpdatePayload();

      console.log("UPDATE PAYLOAD:", payload);

      await updateTour(id, payload);

      toast.success("Tour updated successfully!");
      navigate(`/tours?bookingFilter=${previousBookingFilter}`, {
        state: { refresh: true },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to update tour");
    } finally {
      setLoading(false);
    }
  };

  const handleSendForApproval = async () => {
    try {
      if (!validateArrivalDeparture()) {
        setActiveTab("arrival-departure");
        return;
      }

      if (!validateVenueSummary()) {
        setActiveTab("venue-summary");
        return;
      }

      if (!id) {
        toast.error("Missing tour ID");
        return;
      }

      setLoading(true);

      // Save latest edits first
      await loadTermsAndConditions();

      const payload = buildUpdatePayload();
      console.log("FORM DATA:", formData);
      console.log("PAYLOAD:", payload);
      console.log("UPDATE PAYLOAD", payload);
      await updateTour(id, payload);

      // Then submit for approval
      await updateTourStatus(id, user?.tourOperatorId ?? 1);

      toast.success("Tour updated and submitted successfully for approval!");

      navigate(`/tours?bookingFilter=${previousBookingFilter}`, {
        state: { refresh: true },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to submit tour for approval");
    } finally {
      setLoading(false);
    }
  };

  const handleVenueAvailabilityRequest = async () => {
    try {
      if (!selectedRecommendedDate) {
        toast.error("Please accept one of the recommended dates.");
        return;
      }

      if (!validateArrivalDeparture()) {
        setActiveTab("arrival-departure");
        return;
      }

      if (!validateVenueSummary()) {
        setActiveTab("venue-summary");
        return;
      }

      if (!id) {
        toast.error("Missing tour ID");
        return;
      }

      setLoading(true);

      // STEP 1 - Save latest changes
      await loadTermsAndConditions();

      const payload = buildUpdatePayload();

      await updateTour(id, payload);

      // STEP 2 - Request venue availability
      await requestVenueAvailability(selectedRecommendedDate);

      toast.success("Tour updated and resubmitted successfully!");

      navigate(`/tours?bookingFilter=${previousBookingFilter}`, {
        state: { refresh: true },
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to request venue availability");
    } finally {
      setLoading(false);
    }
  };

  const getTourTypeLabel = () => {
    const category = tourCategory;

    switch (category) {
      case 2:
        return "Miqaat Tour";

      case 3:
        return "Faiz Tour";

      case 4:
        return "External Tour";

      default:
        return "-";
    }
  };

  const isOutsideTourRange = (date: Date) => {
    if (!formData.arrivalDate || !formData.departureDate) return false;

    const d = new Date(date);
    const start = new Date(formData.arrivalDate);
    const end = new Date(formData.departureDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);

    return d < start || d > end;
  };
  useEffect(() => {
    setPageHeader({
      title: "Tour Details",
      subtitle: formData.tourName,
      onBack: async () => {
        try {
          setLoading(true);
          navigate(`/tours?bookingFilter=${previousBookingFilter}`);
        } finally {
          setLoading(false);
        }
      },
    });

    return () => setPageHeader(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.tourName,
    navigate,
    previousBookingFilter,
    setPageHeader,
    tourStatus,
  ]);
  const latestReviewMessage = reviewConversation[reviewConversation.length - 1];
  const hasNewAdminMessage = latestReviewMessage?.sender === "backoffice";

  const renderTourOperatorRequestAccordion = () => (
    <Accordion type="multiple" defaultValue={[]} className="w-full">
      <AccordionItem
        value="tour-operator-request"
        className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm"
      >
        <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
          <div
            className="flex w-full flex-col gap-3 pr-3 text-left sm:flex-row sm:items-start sm:justify-between"
            style={{ alignItems: "center" }}
          >
            <div className="flex items-start gap-3">
              <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#5B9BD5] shadow-sm">
                <MessageSquareText className="h-5 w-5" />
                {hasNewAdminMessage && (
                  <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-white bg-red-500" />
                )}
              </span>
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Tour Operator Request
                </h2>
                <p className="mt-1 text-xs font-normal text-gray-600">
                  Conversation between tour operator and admin.
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {hasNewAdminMessage && (
                <Badge className="border-red-300 bg-red-100 text-red-700 hover:bg-red-100">
                  New message
                </Badge>
              )}
              <Badge className="border-[#5B9BD5]/30 bg-white text-[#5B9BD5] hover:bg-white">
                {reviewConversation.length} messages
              </Badge>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-5 pb-5 pt-0">
          <div className="space-y-3">
            {reviewConversation.length > 0 ? (
              <div className="rounded-lg border border-[#5B9BD5]/20 bg-sky-50/40 p-3">
                <div className="max-h-[360px] space-y-2 overflow-y-auto pb-2 pr-1">
                  {reviewConversation.map((msg, index) => {
                    const isLatest = index === reviewConversation.length - 1;
                    const isAdmin = msg.sender === "backoffice";

                    return (
                      <div
                        key={msg.id}
                        className={`flex px-0.5 ${
                          isAdmin ? "justify-start" : "justify-end"
                        }`}
                      >
                        <div
                          className={`max-w-[78%] rounded-2xl border px-3 py-2 shadow-sm ${
                            isAdmin
                              ? "rounded-bl-sm border-blue-200 bg-white"
                              : "rounded-br-sm border-green-200 bg-green-50"
                          } ${isLatest ? "ring-1 ring-red-300" : ""}`}
                        >
                          <div className="mb-1 flex items-center justify-between gap-3">
                            <div className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate text-[11px] font-bold text-gray-900">
                                {msg.senderName}
                              </span>
                              <Badge
                                className={
                                  isAdmin
                                    ? "border-blue-300 bg-blue-100 px-1.5 py-0 text-[9px] text-blue-700"
                                    : "border-green-300 bg-green-100 px-1.5 py-0 text-[9px] text-green-700"
                                }
                              >
                                {isAdmin ? "Admin" : "TO"}
                              </Badge>
                              {isLatest && (
                                <Badge className="border-red-300 bg-red-100 px-1.5 py-0 text-[9px] text-red-700">
                                  Latest
                                </Badge>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="text-[10px] font-semibold text-gray-500">
                                {msg.timestamp}
                              </p>
                              <p className="text-[9px] text-gray-400">
                                {msg.dateTime}
                              </p>
                            </div>
                          </div>
                          <p className="whitespace-pre-line text-xs leading-relaxed text-gray-800">
                            {msg.message}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-gray-300 bg-gradient-to-br from-gray-50 to-blue-50 p-4">
                <p className="text-center text-xs text-gray-600 md:text-sm">
                  No conversation messages yet.
                </p>
              </div>
            )}

            <div className="rounded-lg border border-blue-200 bg-blue-50/60 p-2">
              <div className="grid grid-cols-[1fr_auto] items-center gap-2">
                <textarea
                  value={responseToAdmin}
                  onChange={(e) => setResponseToAdmin(e.target.value)}
                  placeholder="Type response to admin..."
                  className="h-10 min-h-10 w-full resize-none rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm leading-5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#5B9BD5]"
                />
                <Button
                  size="icon"
                  className="h-10 w-10 text-white"
                  style={{ backgroundColor: "#5B9BD5" }}
                  onClick={handleSendReviewResponse}
                  disabled={!responseToAdmin.trim()}
                  title="Send Response"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="min-h-screen bg-background">
        <div>
          {/* Tabs */}
          <div
            className="mb-4 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between"
            style={{ marginTop: "0px" }}
          >
            <div className="flex flex-wrap items-center gap-0">
              {[
                {
                  id: "arrival-departure",
                  label: "Arrival & Departure",
                  icon: Plane,
                },
                { id: "venue-summary", label: "Venue Summary", icon: FileText },
                { id: "confirmation", label: "Confirmation", icon: FileText },
              ].map(({ id, label, icon: Icon }, index) => {
                const isEnabled = true;

                return (
                  <button
                    key={id}
                    onClick={() => {
                      if (isEnabled) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        setActiveTab(id as any);
                      }
                    }}
                    disabled={!isEnabled}
                    className={`
                    flex items-center gap-2 px-5 py-3 relative text-sm
                    ${activeTab === id ? "text-primary" : "text-muted-foreground"}
                    ${
                      !isEnabled
                        ? "opacity-40 cursor-not-allowed"
                        : "hover:text-foreground"
                    }
                  `}
                  >
                    <div
                      className={`
                      w-7 h-7 rounded-full flex items-center justify-center font-semibold text-xs
                      ${
                        activeTab === id
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      }
                    `}
                    >
                      {index + 1}
                    </div>

                    <span className="font-medium">{label}</span>

                    {activeTab === id && isEnabled && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center justify-end mb-3">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                {tourStatus !== null && (
                  <Badge
                    className={`${statusMeta.className} relative mb-3 w-fit px-3 py-1 pr-7 text-xs sm:mb-0 sm:text-sm`}
                  >
                    {statusName}
                    {isLocked && (
                      <LockKeyhole
                        className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2"
                        style={{ color: "red" }}
                        aria-hidden="true"
                      />
                    )}
                  </Badge>
                )}

                {tourStatus !== null && (
                  <Badge className="bg-purple-100 text-purple-700 border-purple-300 px-3 py-1 text-xs sm:text-sm">
                    {tourCode}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {/* Tab 1: Arrival & Departure */}
            {activeTab === "arrival-departure" && (
              <div className="space-y-5">
                {/* Recommended Dates Section (shown when status is recommended) */}
                {tourStatus === 3 && recommendedDates.length > 0 && (
                  <Card
                    className="border-2 border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base text-yellow-900 flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-yellow-600" />
                            Recommended Alternative Dates
                          </CardTitle>
                          <p className="text-xs text-yellow-700 mt-1">
                            Rooms are available on these dates. Select one to
                            automatically update arrival and departure dates.
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {recommendedDates.map((dateOption) => (
                        <div
                          key={dateOption.recommendPublicId}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedRecommendedDate ===
                            dateOption.recommendPublicId
                              ? "border-yellow-500 bg-yellow-100"
                              : "border-yellow-200 bg-white hover:border-yellow-400 hover:bg-yellow-50"
                          }`}
                          onClick={() =>
                            setSelectedRecommendedDate(
                              dateOption.recommendPublicId,
                            )
                          }
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="grid grid-cols-2 gap-4 mb-3">
                                <div>
                                  <Label className="text-xs font-semibold text-gray-600">
                                    Arrival
                                  </Label>
                                  <p className="text-sm font-medium text-gray-900">
                                    {new Date(
                                      dateOption.requestedStartDate,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}{" "}
                                    {/* at {dateOption.requestedStartDate} */}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs font-semibold text-gray-600">
                                    Departure
                                  </Label>
                                  <p className="text-sm font-medium text-gray-900">
                                    {new Date(
                                      dateOption.requestedEndDate,
                                    ).toLocaleDateString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                    })}{" "}
                                    {/* at {dateOption.requestedEndDate} */}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs text-gray-700">
                                  <strong>Reason:</strong> {dateOption.reason}
                                </p>
                                {/* <p className="text-xs text-gray-500">
                                  Recommended on{" "}
                                  {new Date(dateOption.recommendedOn).toLocaleString()}
                                </p> */}
                                {/* <div className="flex gap-2 flex-wrap">
                                  <Badge className="bg-purple-100 text-purple-700 text-xs">
                                    Deluxe: {dateOption.roomsAvailable.deluxe}{" "}
                                    rooms
                                  </Badge>
                                  <Badge className="bg-blue-100 text-blue-700 text-xs">
                                    Standard:{" "}
                                    {dateOption.roomsAvailable.standard} rooms
                                  </Badge>
                                  <Badge className="bg-cyan-100 text-cyan-700 text-xs">
                                    Shared Gents:{" "}
                                    {dateOption.roomsAvailable.sharedGents}{" "}
                                    rooms
                                  </Badge>
                                  <Badge className="bg-pink-100 text-pink-700 text-xs">
                                    Shared Ladies:{" "}
                                    {dateOption.roomsAvailable.sharedLadies}{" "}
                                    rooms
                                  </Badge>
                                </div> */}
                              </div>
                            </div>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAcceptRecommendedDate(dateOption);
                              }}
                              style={{ background: "#10B981" }}
                              className="text-white"
                              size="sm"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Accept Dates
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Arrival and Departure Cards Side by Side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                  {/* Arrival Card */}
                  <Card
                    className="bg-white border-2 border-primary/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                            <ArrowRight className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <CardTitle className="text-base font-semibold text-foreground">
                            Arrival Details
                          </CardTitle>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <CardTitle className="text-sm font-semibold block text-foreground">
                            Transport Mode
                          </CardTitle>
                          <Badge
                            className={
                              formData.arrivalTransportMode === "flight"
                                ? "bg-sky-100 text-sky-700 border-sky-300 px-2 py-1 text-xs sm:text-sm"
                                : formData.arrivalTransportMode === "road"
                                  ? "bg-amber-100 text-amber-700 border-amber-300 px-3 py-1 text-xs sm:text-sm"
                                  : "bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1 text-xs sm:text-sm"
                            }
                          >
                            {formData.arrivalTransportMode === "flight"
                              ? "By Flight"
                              : formData.arrivalTransportMode === "road"
                                ? "By Road"
                                : "By Sea"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {formData.arrivalTransportMode === "flight" ? (
                        <>
                          {/* Row 1 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.arrivalDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  disabledDates={portBlockedDates}
                                  cutoffDates={cutoffBlockedDates}
                                  month={
                                    arrivalCalendarMonth ??
                                    (formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined)
                                  }
                                  onMonthChange={(month) => {
                                    setArrivalCalendarMonth(month);
                                    setDepartureCalendarMonth(month);
                                  }}
                                  minDate={
                                    formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined
                                  }
                                  disabled={(date) =>
                                    formData.arrivalConfirmed ||
                                    isDateDisabled(date, "arrival")
                                  }
                                  isDisabled={
                                    formData.arrivalConfirmed || isReadOnly
                                  }
                                  onChange={(date) => {
                                    const formatted = formatLocalDate(date);

                                    setManualDateEdit(false);

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalDate: formatted,

                                      // Clear departure date if arrival becomes greater
                                      departureDate:
                                        prev.departureDate &&
                                        new Date(formatted) >
                                          new Date(prev.departureDate)
                                          ? ""
                                          : prev.departureDate,
                                    }));

                                    setRefreshKey((k) => k + 1);
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Flight No."
                                required
                                placeholder="IQ2022 "
                                className="text-xs"
                                value={formData.arrivalFlightNumber}
                                onChange={(e) => {
                                  let value = e.target.value.toUpperCase();

                                  value = value.replace(/[^A-Z0-9]/g, "");

                                  // First 2 chars must be:
                                  // AA  OR  A1
                                  if (value.length <= 2) {
                                    if (
                                      !/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(value)
                                    ) {
                                      return;
                                    }
                                  }

                                  // After first 2 chars only digits allowed
                                  if (value.length > 2) {
                                    const prefix = value.substring(0, 2);
                                    const digits = value.substring(2);

                                    const validPrefix =
                                      /^[A-Z]{2}$/.test(prefix) ||
                                      /^[A-Z][0-9]$/.test(prefix);

                                    if (!validPrefix) {
                                      return;
                                    }

                                    if (!/^\d{0,4}$/.test(digits)) {
                                      return;
                                    }
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalFlightNumber: value,
                                  }));
                                }}
                                disabled={
                                  formData.arrivalConfirmed || isReadOnly
                                }
                              />
                              {errors.arrivalFlightNumber && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalFlightNumber}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Row 2 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.arrivalTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    arrivalTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                                disabled={
                                  formData.arrivalConfirmed || isReadOnly
                                }
                              />
                              {errors.arrivalTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalTime}
                                </p>
                              )}
                            </div>
                            <div>
                              <SelectFieldset label="Airport" required>
                                <Select
                                  value={formData.arrivalAirport?.toString()}
                                  onValueChange={(value) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalAirport: Number(value),
                                    }))
                                  }
                                  disabled={
                                    formData.arrivalConfirmed || isReadOnly
                                  }
                                >
                                  <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {ports.map((port) => (
                                      <SelectItem
                                        key={port.portId}
                                        value={port.portId.toString()}
                                      >
                                        {port.portName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.arrivalAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalAirport}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Row 3 */}
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <div>
                              <InputFieldset
                                label="Airline"
                                type="text"
                                placeholder="Enter airline"
                                className="text-xs"
                                maxLength={60}
                                value={formData.arrivalAirline}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (!/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)) {
                                    return;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalAirline: value,
                                  }));
                                }}
                                disabled={
                                  formData.arrivalConfirmed || isReadOnly
                                }
                              />

                              {errors.arrivalAirline && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalAirline}
                                </p>
                              )}
                            </div>

                            <div>
                              <InputFieldset
                                label="Booking Ref"
                                placeholder="Booking Ref"
                                className="text-xs"
                                maxLength={16}
                                value={formData.arrivalBookingRef}
                                onChange={(e) => {
                                  const value = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "");

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalBookingRef: value,
                                  }));
                                }}
                                disabled={
                                  formData.arrivalConfirmed || isReadOnly
                                }
                              />
                              {errors.arrivalBookingRef && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalBookingRef}
                                </p>
                              )}
                            </div>

                            <div className="pb-0.5">
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                              <div className="flex items-center h-9">
                                {arrivalConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.arrivalConfirmed}
                                    disabled={isReadOnly}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.arrivalDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  disabledDates={portBlockedDates}
                                  cutoffDates={cutoffBlockedDates}
                                  month={
                                    arrivalCalendarMonth ??
                                    (formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined)
                                  }
                                  onMonthChange={(month) => {
                                    setArrivalCalendarMonth(month);
                                    setDepartureCalendarMonth(month);
                                  }}
                                  minDate={
                                    formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined
                                  }
                                  disabled={(date) =>
                                    formData.arrivalConfirmed ||
                                    isDateDisabled(date, "arrival")
                                  }
                                  isDisabled={
                                    formData.arrivalConfirmed || isReadOnly
                                  }
                                  onChange={(date) => {
                                    const formatted = formatLocalDate(date);

                                    setManualDateEdit(false);

                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalDate: formatted,

                                      // Clear departure date if arrival becomes greater
                                      departureDate:
                                        prev.departureDate &&
                                        new Date(formatted) >
                                          new Date(prev.departureDate)
                                          ? ""
                                          : prev.departureDate,
                                    }));

                                    setRefreshKey((k) => k + 1);
                                  }}
                                />
                              </SelectFieldset>
                            </div>
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.arrivalTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    arrivalTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    arrivalTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                                disabled={
                                  formData.arrivalConfirmed || isReadOnly
                                }
                              />
                              {errors.arrivalTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalTime}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            <div>
                              <SelectFieldset label="Port" required>
                                <Select
                                  value={formData.arrivalAirport?.toString()}
                                  onValueChange={(value) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      arrivalAirport: Number(value),
                                    }))
                                  }
                                  disabled={
                                    formData.arrivalConfirmed || isReadOnly
                                  }
                                >
                                  <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {ports.map((port) => (
                                      <SelectItem
                                        key={port.portId}
                                        value={port.portId.toString()}
                                      >
                                        {port.portName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.arrivalAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.arrivalAirport}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-5">
                              <div className="flex items-center h-9">
                                {arrivalConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.arrivalConfirmed}
                                    disabled={isReadOnly}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        arrivalConfirmed: checked,
                                      }))
                                    }
                                  />
                                )}
                              </div>
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Departure Card */}
                  <Card
                    className="bg-white border-2 border-destructive/40 shadow-sm"
                    style={{ gap: "0px" }}
                  >
                    <CardHeader className="bg-card" style={{ gap: "0px" }}>
                      <div className="flex items-center justify-between">
                        <div className="flex flex-wrap gap-2 items-center">
                          <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center">
                            <Plane className="w-5 h-5 text-primary-foreground" />
                          </div>
                          <CardTitle className="text-base font-semibold text-foreground">
                            Departure Details
                          </CardTitle>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <CardTitle className="text-sm font-semibold block text-foreground">
                            Transport Mode
                          </CardTitle>
                          <Badge
                            className={
                              formData.departureTransportMode === "flight"
                                ? "bg-sky-100 text-sky-700 border-sky-300 px-2 py-1 text-xs sm:text-sm"
                                : formData.departureTransportMode === "road"
                                  ? "bg-amber-100 text-amber-700 border-amber-300 px-3 py-1 text-xs sm:text-sm"
                                  : "bg-emerald-100 text-emerald-700 border-emerald-300 px-3 py-1 text-xs sm:text-sm"
                            }
                          >
                            {formData.departureTransportMode === "flight"
                              ? "By Flight"
                              : formData.departureTransportMode === "road"
                                ? "By Road"
                                : "By Sea"}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>

                    <CardContent className="space-y-4 pt-4 bg-card">
                      {formData.departureTransportMode === "flight" ? (
                        <>
                          {/* Row 1 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.departureDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  disabledDates={portBlockedDates}
                                  cutoffDates={cutoffBlockedDates}
                                  month={
                                    departureCalendarMonth ??
                                    (formData.departureDate
                                      ? new Date(formData.departureDate)
                                      : undefined)
                                  }
                                  onMonthChange={(month) => {
                                    setDepartureCalendarMonth(month);
                                  }}
                                  minDate={
                                    formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined
                                  }
                                  disabled={(date) =>
                                    formData.departureConfirmed ||
                                    isReadOnly ||
                                    isDateDisabled(date, "departure")
                                  }
                                  isDisabled={
                                    formData.departureConfirmed || isReadOnly
                                  }
                                  onChange={(date: Date) => {
                                    const formatted = formatLocalDate(date);

                                    setManualDateEdit(false);

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureDate: formatted,
                                    }));

                                    setRefreshKey((k) => k + 1);
                                  }}
                                />
                              </SelectFieldset>
                              {errors.departureDate && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureDate}
                                </p>
                              )}
                            </div>
                            <div>
                              <InputFieldset
                                label="Flight No."
                                required
                                placeholder="IQ2022"
                                className="text-xs"
                                value={formData.departureFlightNumber}
                                onChange={(e) => {
                                  let value = e.target.value.toUpperCase();

                                  value = value.replace(/[^A-Z0-9]/g, "");

                                  if (value.length <= 2) {
                                    if (
                                      !/^([A-Z]{0,2}|[A-Z][0-9]?)$/.test(value)
                                    ) {
                                      return;
                                    }
                                  }

                                  if (value.length > 2) {
                                    const prefix = value.substring(0, 2);
                                    const digits = value.substring(2);

                                    const validPrefix =
                                      /^[A-Z]{2}$/.test(prefix) ||
                                      /^[A-Z][0-9]$/.test(prefix);

                                    if (!validPrefix) {
                                      return;
                                    }

                                    if (!/^\d{0,4}$/.test(digits)) {
                                      return;
                                    }
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureFlightNumber: value,
                                  }));
                                }}
                                disabled={
                                  formData.departureConfirmed || isReadOnly
                                }
                              />
                              {errors.departureFlightNumber && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureFlightNumber}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Row 2 */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.departureTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    departureTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                                disabled={
                                  formData.departureConfirmed || isReadOnly
                                }
                              />
                              {errors.departureTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureTime}
                                </p>
                              )}
                            </div>
                            <div>
                              <SelectFieldset label="Airport" required>
                                <Select
                                  value={formData.departureAirport?.toString()}
                                  onValueChange={(value) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      departureAirport: Number(value),
                                    }))
                                  }
                                  disabled={
                                    formData.departureConfirmed || isReadOnly
                                  }
                                >
                                  <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {ports.map((port) => (
                                      <SelectItem
                                        key={port.portId}
                                        value={port.portId.toString()}
                                      >
                                        {port.portName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.departureAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureAirport}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Row 3 */}
                          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <div>
                              <InputFieldset
                                label="Airline"
                                type="text"
                                placeholder="Enter airline"
                                className="text-xs"
                                maxLength={60}
                                value={formData.departureAirline}
                                onChange={(e) => {
                                  const value = e.target.value;

                                  if (!/^[\p{L}\p{M}\p{N}\s-]*$/u.test(value)) {
                                    return;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureAirline: value,
                                  }));
                                }}
                                disabled={
                                  formData.departureConfirmed || isReadOnly
                                }
                              />
                              {errors.departureAirline && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureAirline}
                                </p>
                              )}
                            </div>
                            <div>
                              <InputFieldset
                                label="Booking Ref"
                                placeholder="Booking Ref"
                                className="text-xs"
                                maxLength={16}
                                value={formData.departureBookingRef}
                                onChange={(e) => {
                                  const value = e.target.value
                                    .toUpperCase()
                                    .replace(/[^A-Z0-9]/g, "");

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureBookingRef: value,
                                  }));
                                }}
                                disabled={
                                  formData.departureConfirmed || isReadOnly
                                }
                              />
                              {errors.departureBookingRef && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureBookingRef}
                                </p>
                              )}
                            </div>

                            <div className="pb-0.5">
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                              <div className="flex items-center h-9">
                                {departureConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.departureConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        departureConfirmed: checked,
                                      }))
                                    }
                                    disabled={isReadOnly}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <SelectFieldset label="Date" required>
                                <CustomDayPicker
                                  value={formData.departureDate}
                                  operatorExpiryDate={
                                    operatorExpiryDate ?? undefined
                                  }
                                  disabledDates={portBlockedDates}
                                  cutoffDates={cutoffBlockedDates}
                                  month={
                                    departureCalendarMonth ??
                                    (formData.departureDate
                                      ? new Date(formData.departureDate)
                                      : undefined)
                                  }
                                  onMonthChange={(month) => {
                                    setDepartureCalendarMonth(month);
                                  }}
                                  minDate={
                                    formData.arrivalDate
                                      ? new Date(formData.arrivalDate)
                                      : undefined
                                  }
                                  disabled={(date) =>
                                    formData.departureConfirmed ||
                                    isReadOnly ||
                                    isDateDisabled(date, "departure")
                                  }
                                  isDisabled={
                                    formData.departureConfirmed || isReadOnly
                                  }
                                  onChange={(date: Date) => {
                                    const formatted = formatLocalDate(date);

                                    setManualDateEdit(false);

                                    setFormData((prev) => ({
                                      ...prev,
                                      departureDate: formatted,
                                    }));

                                    setRefreshKey((k) => k + 1);
                                  }}
                                />
                              </SelectFieldset>
                              {errors.departureDate && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureDate}
                                </p>
                              )}
                            </div>
                            <div>
                              <InputFieldset
                                label="Time (24hr Format)"
                                required
                                type="text"
                                placeholder="HH:MM"
                                maxLength={5}
                                className="text-xs"
                                value={formData.departureTime}
                                onChange={(e) => {
                                  let value = e.target.value.replace(
                                    /[^\d]/g,
                                    "",
                                  );

                                  if (value.length > 4) return;

                                  if (value.length >= 3) {
                                    value = `${value.slice(0, 2)}:${value.slice(2)}`;
                                  }

                                  setFormData((prev) => ({
                                    ...prev,
                                    departureTime: value,
                                  }));

                                  setErrors((prev) => ({
                                    ...prev,
                                    departureTime:
                                      value.length > 0 && !isValidTime(value)
                                        ? "Time must be in 24-hour format (HH:MM)"
                                        : "",
                                  }));
                                }}
                                disabled={
                                  formData.departureConfirmed || isReadOnly
                                }
                              />
                              {errors.departureTime && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureTime}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                            <div>
                              <SelectFieldset label="Airport" required>
                                <Select
                                  value={formData.departureAirport?.toString()}
                                  onValueChange={(value) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      departureAirport: Number(value),
                                    }))
                                  }
                                  disabled={
                                    formData.departureConfirmed || isReadOnly
                                  }
                                >
                                  <SelectTriggerUI className="text-xs">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTriggerUI>

                                  <SelectContent>
                                    {ports.map((port) => (
                                      <SelectItem
                                        key={port.portId}
                                        value={port.portId.toString()}
                                      >
                                        {port.portName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </SelectFieldset>
                              {errors.departureAirport && (
                                <p className="text-xs text-red-500 mt-1">
                                  {errors.departureAirport}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-5">
                              <div className="flex items-center h-9">
                                {departureConfirmedFromApi ? (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Yes
                                  </Badge>
                                ) : (
                                  <Switch
                                    checked={formData.departureConfirmed}
                                    onCheckedChange={(checked) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        departureConfirmed: checked,
                                      }))
                                    }
                                    disabled={isReadOnly}
                                  />
                                )}
                              </div>
                              <Label className="text-xs font-semibold mb-1.5 block text-muted-foreground">
                                Confirmed
                              </Label>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Tab 2: Venue Summary */}
            {activeTab === "venue-summary" && (
              <div className="space-y-5">
                <Card
                  className="bg-white border border-border shadow-sm"
                  style={{ gap: "0px" }}
                >
                  <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold">
                      Venue Summary
                    </CardTitle>
                  </CardHeader>

                  <CardContent style={{ padding: "0px 24px 20px 24px" }}>
                    <div
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                      style={{ paddingLeft: "16px", paddingRight: "16px" }}
                    >
                      {/* Tour Name */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="Tour Name"
                          required
                          placeholder="Enter tour name"
                          className="text-sm focus:ring-2 focus:ring-primary/30"
                          maxLength={50}
                          value={formData.tourName}
                          disabled
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value.length > 50) return;

                            if (!/^[A-Za-z0-9 ]*$/.test(value)) return;

                            setFormData((prev) => ({
                              ...prev,
                              tourName: value,
                            }));
                          }}
                        />

                        {errors.tourName && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.tourName}
                          </p>
                        )}
                      </div>

                      {/* Applicants */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="No. of Applicants"
                          required
                          type="number"
                          placeholder="0"
                          min={0}
                          max={200}
                          className="text-sm"
                          value={formData.noOfApplicants}
                          disabled={isReadOnly}
                          onChange={(e) => {
                            const value = e.target.value;

                            if (value === "") {
                              setFormData((prev) => ({
                                ...prev,
                                noOfApplicants: "",
                              }));
                              return;
                            }

                            const num = Number(value);

                            if (num < 0 || num > 200) return;

                            setFormData((prev) => ({
                              ...prev,
                              noOfApplicants: value,
                            }));

                            if (errors.noOfApplicants) {
                              setErrors((prev) => ({
                                ...prev,
                                noOfApplicants: "",
                              }));
                            }
                          }}
                        />
                        {errors.noOfApplicants && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.noOfApplicants}
                          </p>
                        )}
                      </div>

                      {/* Parent Tour Id */}
                      <div className="md:col-span-3 space-y-2">
                        <InputFieldset
                          label="Parent Tour Id"
                          placeholder="Enter Parent Tour Id"
                          className="text-sm"
                          maxLength={50}
                          value={formData.parentTourId}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase();

                            // Allow only letters, numbers and hyphen
                            if (!/^[A-Z0-9-]*$/.test(value)) {
                              return;
                            }

                            if (value.length > 50) {
                              return;
                            }

                            setFormData((prev) => ({
                              ...prev,
                              parentTourId: value,
                            }));

                            // Clear error while user is correcting the value
                            setErrors((prev) => ({
                              ...prev,
                              parentTourId: "",
                            }));
                          }}
                        />

                        {errors.parentTourId && (
                          <p className="text-xs text-red-500 mt-1">
                            {errors.parentTourId}
                          </p>
                        )}
                      </div>

                      {/* Faiz */}
                      <div className="md:col-span-3 space-y-2">
                        <Label className="text-sm font-medium text-muted-foreground">
                          Faiz Accommodation{" "}
                          <span className="text-red-500">*</span>
                        </Label>
                        <RadioGroup
                          value={formData.faizAccommodation}
                          disabled
                          className="flex gap-6 items-center h-10"
                        >
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="yes"
                              id="faiz-yes"
                              className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>
                            <Label
                              htmlFor="faiz-yes"
                              className="text-sm cursor-pointer"
                            >
                              Yes
                            </Label>
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem
                              value="no"
                              id="faiz-no"
                              className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                            >
                              <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                            </RadioGroupItem>
                            <Label
                              htmlFor="faiz-no"
                              className="text-sm cursor-pointer"
                            >
                              No
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>

                    {formData.faizAccommodation === "yes" && (
                      <Card
                        className="bg-white border-blue-200 bg-blue-50/50 mt-4"
                        style={{ boxShadow: "none" }}
                      >
                        <CardContent style={{ padding: "16px" }}>
                          {/* Occupancy Question */}
                          <div className="mb-4 grid grid-cols-1 lg:grid-cols-12 gap-3 items-center">
                            <div className="lg:col-span-6">
                              <Label className="text-sm font-medium text-gray-900 block">
                                Want to accommodate based on Min or Max
                                Occupancy?
                              </Label>
                            </div>

                            <div className="lg:col-span-6">
                              <RadioGroup
                                value={accommodateBy || ""}
                                onValueChange={(v) =>
                                  setAccommodateBy(v as "minimum" | "maximum")
                                }
                                disabled={isReadOnly}
                                className="flex gap-6 items-center"
                              >
                                {/* Minimum */}
                                <div className="flex items-center gap-2">
                                  <RadioGroupItem
                                    value="minimum"
                                    id="min-occupancy"
                                    className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                                  >
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                  </RadioGroupItem>

                                  <Label
                                    htmlFor="min-occupancy"
                                    className="text-sm cursor-pointer"
                                  >
                                    Min Occupancy
                                  </Label>
                                </div>

                                {/* Maximum */}
                                <div className="flex items-center gap-2">
                                  <RadioGroupItem
                                    value="maximum"
                                    id="max-occupancy"
                                    className="
                                      h-5 w-5 rounded-full
                                      border-2 border-gray-200
                                      flex items-center justify-center
                                      data-[state=checked]:border-primary
                                    "
                                  >
                                    <span className="h-2.5 w-2.5 rounded-full bg-primary opacity-0 data-[state=checked]:opacity-100" />
                                  </RadioGroupItem>

                                  <Label
                                    htmlFor="max-occupancy"
                                    className="text-sm cursor-pointer"
                                  >
                                    Max Occupancy
                                  </Label>
                                </div>
                              </RadioGroup>
                            </div>
                          </div>
                          <div>
                            <InputFieldset
                              label="Any special accommodation request?"
                              placeholder="e.g. Wheelchair accessible, Ground floor, Adjacent rooms..."
                              className="text-sm"
                              value={specialRoomRequestDetails}
                              disabled={isReadOnly}
                              onChange={(e) => {
                                setSpecialRoomRequestDetails(e.target.value);

                                if (errors.specialAccommodationRequest) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    specialAccommodationRequest: "",
                                  }));
                                }
                              }}
                            />

                            {errors.specialAccommodationRequest && (
                              <p className="text-xs text-red-500 mt-1">
                                {errors.specialAccommodationRequest}
                              </p>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="mt-4">
                      {renderTourOperatorRequestAccordion()}
                    </div>

                    {/* ================= VENUE SECTION ================= */}
                    <div className="space-y-4" style={{ marginTop: "14px" }}>
                      {/* Section Header */}
                      <div
                        className="flex"
                        style={{
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div>
                          <h3 className="text-sm font-semibold text-foreground">
                            Venue Schedule{" "}
                            <span className="text-red-500">*</span>
                          </h3>
                          <p className="text-xs text-muted-foreground">
                            Add venues with check-in and check-out details
                          </p>
                        </div>
                        {/* Add Button */}
                        <div
                          className="flex justify-end"
                          style={{ marginTop: "20px" }}
                        >
                          {canAddVenue() && (
                            <Button
                              type="button"
                              className="h-10 px-4 text-sm font-medium"
                              disabled={isReadOnly}
                              onClick={() => {
                                const newVenue = {
                                  id: `venue-${Date.now()}`,
                                  venueId: null,
                                  hotelPropertyId: null,
                                  hotelName: "",
                                  fromDate: "",
                                  fromTime: "",
                                  toDate: "",
                                  toTime: "",
                                };

                                setFormData((prev) => {
                                  const updated = [...prev.venues];
                                  updated.splice(1, 0, newVenue);

                                  return {
                                    ...prev,
                                    venues: updated,
                                  };
                                });
                              }}
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add Venue
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Empty State */}
                      {formData.venues.length === 0 ? (
                        <div className="border border-dashed border-border rounded-lg p-10 text-center">
                          <MapPin className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                          <p className="text-sm text-muted-foreground">
                            No venues added yet
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {formData.venues.map((venue, index) => {
                            const isFirst = index === 0;
                            const isLast = index === formData.venues.length - 1;
                            return (
                              <div
                                key={venue.id}
                                className="border rounded-lg p-4 bg-muted/30 hover:shadow-sm transition"
                              >
                                <div className="flex gap-4">
                                  {/* <div className="w-7 h-7 flex items-center justify-center rounded-full bg-muted text-muted-foreground text-xs font-semibold">
                                                        {index + 1}
                                                      </div> */}
                                  <div className="flex-1 space-y-4">
                                    {/* Venue Name */}
                                    <div>
                                      <div
                                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
                                        style={{ alignItems: "center" }}
                                      >
                                        <div className="space-y-1">
                                          <SelectFieldset
                                            label={`Venue Name ${index + 1}`}
                                            required
                                            error={errors[`venueName-${index}`]}
                                          >
                                            <Select
                                              value={
                                                venue.venueId
                                                  ? venue.venueId.toString()
                                                  : ""
                                              }
                                              disabled={isReadOnly}
                                              onValueChange={(value) => {
                                                const updated = [
                                                  ...formData.venues,
                                                ];
                                                updated[index].venueId =
                                                  Number(value);
                                                setManualDateEdit(false);
                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                            >
                                              <SelectTriggerUI className="h-10 text-sm">
                                                <SelectValue placeholder="Select venue">
                                                  {venue.venueId
                                                    ? venuesFromApi.find(
                                                        (v) =>
                                                          v.venueId ===
                                                          venue.venueId,
                                                      )?.venueName
                                                    : "Select venue"}
                                                </SelectValue>
                                              </SelectTriggerUI>

                                              <SelectContent>
                                                {venuesFromApi.map((v) => (
                                                  <SelectItem
                                                    key={v.venueId}
                                                    value={v.venueId.toString()}
                                                  >
                                                    {v.venueName}
                                                  </SelectItem>
                                                ))}
                                              </SelectContent>
                                            </Select>
                                          </SelectFieldset>
                                        </div>
                                        <div className="space-y-1">
                                          {formData.faizAccommodation ===
                                            "no" && (
                                            <SelectFieldset
                                              label={`Hotel Name ${index + 1}`}
                                            >
                                              <Select
                                                value={
                                                  venue.hotelPropertyId?.toString() ??
                                                  ""
                                                }
                                                onValueChange={(value) => {
                                                  const selectedHotel =
                                                    hotelsByVenue[
                                                      venue.venueId ?? 0
                                                    ]?.find(
                                                      (h) =>
                                                        h.propertyId ===
                                                        Number(value),
                                                    );

                                                  const updated = [
                                                    ...formData.venues,
                                                  ];

                                                  updated[
                                                    index
                                                  ].hotelPropertyId =
                                                    Number(value);
                                                  updated[index].hotelName =
                                                    selectedHotel?.propertyName ??
                                                    "";

                                                  setFormData((prev) => ({
                                                    ...prev,
                                                    venues: updated,
                                                  }));
                                                }}
                                              >
                                                <SelectTriggerUI className="h-10 text-sm">
                                                  <SelectValue>
                                                    {venue.hotelName ||
                                                      hotelsByVenue[
                                                        venue.venueId ?? 0
                                                      ]?.find(
                                                        (h) =>
                                                          Number(
                                                            h.propertyId,
                                                          ) ===
                                                          Number(
                                                            venue.hotelPropertyId,
                                                          ),
                                                      )?.propertyName ||
                                                      "Select hotel"}
                                                  </SelectValue>
                                                </SelectTriggerUI>

                                                <SelectContent>
                                                  {(
                                                    hotelsByVenue[
                                                      venue.venueId ?? 0
                                                    ] ?? []
                                                  ).map((hotel) => (
                                                    <SelectItem
                                                      key={hotel.propertyId}
                                                      value={hotel.propertyId.toString()}
                                                    >
                                                      {hotel.propertyName}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            </SelectFieldset>
                                          )}
                                        </div>
                                        <div className="space-y-1"></div>
                                        <div
                                          className="space-y-1"
                                          style={{
                                            display: "flex",
                                            justifyContent: "end",
                                          }}
                                        >
                                          {/* Delete */}
                                          {!(isFirst || isLast) && (
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="icon"
                                              className="h-8 w-8 text-destructive hover:bg-destructive/10"
                                              onClick={() => {
                                                const updated =
                                                  formData.venues.filter(
                                                    (_, i) => i !== index,
                                                  );

                                                setManualDateEdit(false); // allow auto recalculation

                                                setFormData((prev) => ({
                                                  ...prev,
                                                  venues: updated,
                                                }));
                                              }}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Dates */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                      {/* Check-In Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-In Date"
                                          required
                                          error={errors[`fromDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.fromDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            month={
                                              venueCalendarMonths[
                                                `checkin-${index}`
                                              ] ??
                                              (venue.fromDate
                                                ? new Date(venue.fromDate)
                                                : undefined)
                                            }
                                            onMonthChange={(month) => {
                                              setVenueCalendarMonths(
                                                (prev) => ({
                                                  ...prev,
                                                  [`checkin-${index}`]: month,
                                                }),
                                              );
                                            }}
                                            disabledDates={allBlockedDates}
                                            highlightRange={{
                                              from: formData.arrivalDate,
                                              to: formData.departureDate,
                                            }}
                                            disabled={(date) =>
                                              isFirst ||
                                              isDateDisabled(date, "arrival") ||
                                              isOutsideTourRange(date)
                                            }
                                            onChange={(date: Date) => {
                                              if (isFirst) return;

                                              setManualDateEdit(true);

                                              const formatted =
                                                formatLocalDate(date);

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromDate =
                                                formatted;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>
                                      </div>

                                      {/* Check-In Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-In Time"
                                            required
                                            type="time"
                                            className={`text-sm pr-8 border border-gray-300 rounded-md transition-all outline-none ${
                                              isFirst
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.fromTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isFirst) return;

                                              const updated = [
                                                ...formData.venues,
                                              ];
                                              updated[index].fromTime =
                                                e.target.value;

                                              setFormData((prev) => ({
                                                ...prev,
                                                venues: updated,
                                              }));
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkin"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkin",
                                            )}
                                          />
                                        </div>
                                        {errors[`fromTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`fromTime-${index}`]}
                                          </p>
                                        )}
                                      </div>

                                      {/* Check-Out Date */}
                                      <div className="space-y-1">
                                        <SelectFieldset
                                          label="Check-Out Date"
                                          required
                                          error={errors[`toDate-${index}`]}
                                        >
                                          <CustomDayPicker
                                            value={venue.toDate}
                                            operatorExpiryDate={
                                              operatorExpiryDate ?? undefined
                                            }
                                            mode="range"
                                            month={
                                              venueCalendarMonths[
                                                `checkout-${index}`
                                              ] ??
                                              (venue.toDate
                                                ? new Date(venue.toDate)
                                                : undefined)
                                            }
                                            onMonthChange={(month) => {
                                              setVenueCalendarMonths(
                                                (prev) => ({
                                                  ...prev,
                                                  [`checkout-${index}`]: month,
                                                }),
                                              );
                                            }}
                                            highlightRange={{
                                              from: formData.arrivalDate,
                                              to: formData.departureDate,
                                            }}
                                            disabledDates={allBlockedDates}
                                            disabled={(date) =>
                                              isLast ||
                                              isDateDisabled(
                                                date,
                                                "departure",
                                              ) ||
                                              isOutsideTourRange(date)
                                            }
                                            onChange={(date: Date) => {
                                              const formatted =
                                                formatLocalDate(date);

                                              setManualDateEdit(true);

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];
                                                updated[index].toDate =
                                                  formatted;

                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromDate =
                                                    formatted;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                            placeholder="dd-MM-yyyy"
                                          />
                                        </SelectFieldset>
                                      </div>

                                      {/* Check-Out Time */}
                                      <div className="space-y-1">
                                        <div className="relative group">
                                          <InputFieldset
                                            label="Check-Out Time"
                                            required
                                            type="time"
                                            className={`text-sm border border-gray-300 rounded-md transition-all outline-none ${
                                              isLast
                                                ? "text-gray-600 border-gray-200 cursor-not-allowed opacity-70"
                                                : ""
                                            }`}
                                            value={venue.toTime}
                                            disabled
                                            onChange={(e) => {
                                              if (isLast) return;

                                              setManualDateEdit(true);

                                              setFormData((prev) => {
                                                const updated = [
                                                  ...prev.venues,
                                                ];

                                                updated[index].toTime =
                                                  e.target.value;
                                                if (
                                                  index <
                                                  updated.length - 1
                                                ) {
                                                  updated[index + 1].fromTime =
                                                    e.target.value;
                                                }

                                                return {
                                                  ...prev,
                                                  venues: updated,
                                                };
                                              });
                                            }}
                                          />
                                          <CalculationBox
                                            type="checkout"
                                            index={index}
                                            isLast={
                                              index ===
                                              formData.venues.length - 1
                                            }
                                            data={getCalculationBreakdown(
                                              venue,
                                              index,
                                              "checkout",
                                            )}
                                          />
                                        </div>
                                        {errors[`toTime-${index}`] && (
                                          <p className="text-xs text-red-500 mt-1">
                                            {errors[`toTime-${index}`]}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Tab 3: Confirmation */}
            {activeTab === "confirmation" && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <Card
                    className="bg-white w-full h-full"
                    style={{ marginTop: "0px" }}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        {/* Tour Name */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Tour Name
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.tourName}
                          </span>
                        </div>

                        {/* Package */}
                        {/* <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="
                            text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Package
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.duration || "-"}
                          </span>
                        </div> */}

                        {/* Tour Type */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Tour Type
                          </span>
                          <span
                            className="text-xs font-semibold text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {getTourTypeLabel()}
                          </span>
                        </div>

                        {/* Applicants */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Applicants
                          </span>
                          <span
                            className="text-xs text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.noOfApplicants} Applicants
                          </span>
                        </div>

                        {/* Faiz Accommodation */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Faiz Accommodation
                          </span>
                          <span
                            className="text-xs font-semibold text-gray-900"
                            style={{ width: "50%" }}
                          >
                            {formData.faizAccommodation === "yes"
                              ? "Yes"
                              : "No"}
                          </span>
                        </div>

                        {/* Arrival */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Arrival
                          </span>
                          <div style={{ width: "50%" }}>
                            <div className="text-xs text-gray-900">
                              {formData.arrivalDate} •
                              {
                                ports.find(
                                  (p) => p.portId === formData.arrivalAirport,
                                )?.portName
                              }
                            </div>

                            <div className="text-[10px] text-gray-600 mt-0.5">
                              Flight {formData.arrivalFlightNumber} •
                              {formData.arrivalTime}
                            </div>
                          </div>
                        </div>

                        {/* Departure */}
                        <div className="flex items-center justify-between py-2 border-b">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Departure
                          </span>
                          <div style={{ width: "50%" }}>
                            <div className="text-xs text-gray-900">
                              {formData.departureDate} •
                              {
                                ports.find(
                                  (p) => p.portId === formData.departureAirport,
                                )?.portName
                              }
                            </div>

                            <div className="text-[10px] text-gray-600 mt-0.5">
                              Flight {formData.departureFlightNumber} •
                              {formData.departureTime}
                            </div>
                          </div>
                        </div>

                        {/* Venues */}
                        <div className="flex items-start justify-between py-2">
                          <span
                            className="text-xs font-medium text-gray-600 uppercase tracking-wide"
                            style={{ width: "50%" }}
                          >
                            Venues
                          </span>
                          <div className="space-y-0.5" style={{ width: "50%" }}>
                            {formData.venues.map((venue, index) => (
                              <div
                                key={index}
                                className="text-xs text-gray-900"
                              >
                                {
                                  venuesFromApi.find(
                                    (v) => v.venueId === venue.venueId,
                                  )?.venueName
                                }{" "}
                                •{venue.fromDate} → {venue.toDate}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Accordion
                    type="multiple"
                    defaultValue={["notes"]}
                    className="w-full h-full"
                  >
                    <AccordionItem
                      value="notes"
                      className="overflow-hidden rounded-xl border border-sky-100 bg-white shadow-sm"
                    >
                      <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
                        <div className="flex items-start gap-3 text-left">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sky-50 text-[#5B9BD5] shadow-sm">
                            <PencilLine className="h-5 w-5" />
                          </span>
                          <div>
                            <h2 className="text-base font-semibold text-gray-900">
                              Notes
                            </h2>
                            <p className="mt-1 text-xs font-normal text-gray-600">
                              Add any special instructions for the approval team
                              before you submit this tour.
                            </p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-5 pb-5 pt-0">
                        <textarea
                          placeholder="Write additional instructions, exceptions, or follow-up notes here..."
                          className="min-h-[260px] w-full resize-none rounded-lg border border-sky-200 bg-slate-50/60 p-4 text-sm text-gray-800 outline-none transition focus:border-[#5B9BD5] focus:bg-white focus:ring-4 focus:ring-[#5B9BD5]/10"
                          value={formData.notes}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              notes: e.target.value,
                            }))
                          }
                        />
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                <Accordion
                  type="multiple"
                  // defaultValue={["terms"]}
                  className="w-full space-y-4"
                >
                  <AccordionItem
                    value="terms"
                    className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm"
                  >
                    <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
                      <div className="flex w-full flex-col gap-3 pr-3 text-left sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#5B9BD5]/10 text-[#5B9BD5]">
                            <BookOpenCheck className="h-5 w-5" />
                          </span>
                          <div>
                            <h2 className="text-base font-semibold text-gray-900">
                              Terms & Conditions
                            </h2>
                            <p className="mt-1 text-xs font-normal text-gray-600">
                              Review the complete terms, then tick the
                              confirmation below.
                            </p>
                          </div>
                        </div>
                        <Badge className="w-fit shrink-0 border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
                          Required before submit
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-5 pt-0">
                      <div className="max-h-[360px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-inner">
                        {termsAndConditions ? (
                          <div
                            className="prose prose-sm max-w-none text-gray-700 [&_*]:max-w-full"
                            dangerouslySetInnerHTML={{
                              __html: termsAndConditions,
                            }}
                          />
                        ) : (
                          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                            Terms and conditions are not available right now.
                          </div>
                        )}
                      </div>

                      <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 transition hover:bg-emerald-50">
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="flex-1">
                          <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                            <ShieldCheck className="h-4 w-4 text-emerald-600" />
                            I have read the terms and confirm the tour details
                            are accurate and complete
                          </span>
                          <span className="mt-1 block text-xs text-gray-600">
                            Check this only after reviewing the notes, tour
                            summary, and terms above.
                          </span>
                        </span>
                      </label>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  {!isReadOnly && (
                    <Button
                      style={{ background: "#5B9BD5" }}
                      className="text-white px-8 h-9 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        if (tourStatus === 3 && selectedRecommendedDate) {
                          handleVenueAvailabilityRequest();
                        } else {
                          handleSendForApproval();
                        }
                      }}
                      disabled={
                        !termsAccepted ||
                        (tourStatus === 3 && !selectedRecommendedDate)
                      }
                    >
                      Send for Approval
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            {currentIndex > 0 ? (
              <Button
                variant="outline"
                onClick={() =>
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  setActiveTab(enabledTabs[currentIndex - 1] as any)
                }
                className="bg-white text-sm h-9"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
            ) : (
              <div />
            )}

            <div className="text-xs text-muted-foreground font-medium">
              {currentIndex + 1} / {enabledTabs.length}
            </div>

            {currentIndex < enabledTabs.length - 1 ? (
              <Button
                onClick={() => {
                  if (activeTab === "arrival-departure") {
                    if (!validateArrivalDeparture()) return;
                  }

                  if (activeTab === "venue-summary") {
                    if (!validateVenueSummary()) return;
                  }

                  setActiveTab(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    enabledTabs[currentIndex + 1] as any,
                  );
                }}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm h-9"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <div />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
